import { useState } from "react";
import { v4 as uuid } from "uuid";
import {
  FilesUpload,
  ConfirmationModal,
  withIntl,
  intlShape,
} from "lcm-iot-commons";
import filesize from "filesize";

type Props = {
  intl: intlShape;
  values: {
    modbusConfig?: $TSFixMe;
    genericModbusConfig?: $TSFixMe;
  };
  onImport: $TSFixMeFunction;
  setFieldValue: $TSFixMeFunction;
  deviceTypeCode: string;
};

export function ModbusImport(props: Props) {
  const { intl, values, onImport, setFieldValue, deviceTypeCode } = props;

  const { t } = intl;
  const [showImportModal, setShowImportModal] = useState(false);
  const [acceptedFile, setAcceptedFile] = useState();
  const [rejectedErrors, setRejectedErrors] = useState([]);

  const whiteList = [".mbconf"];
  const maxFileSize = 1024 * 1024 * 10;

  const updateUIWithLoadedData = ({ data, file }: $TSFixMe) => {
    try {
      const importedData = onImport(JSON.parse(data), deviceTypeCode);

      if (importedData.uploadError) {
        setRejectedErrors([
          // @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'never'.
          { uploadError: "incompatible_config_format_error", file },
        ]);
        return;
      }

      if (importedData.byte_order !== undefined) {
        setFieldValue("byte_order", importedData.byte_order);
      }
      if (importedData.unit_identifier !== undefined) {
        setFieldValue("unit_identifier", importedData.unit_identifier);
      }
      if (importedData.modbusConfig) {
        setFieldValue("modbusConfig", importedData.modbusConfig);
      }
      if (importedData.genericModbusConfig) {
        setFieldValue("genericModbusConfig", importedData.genericModbusConfig);
      }
    } catch {
      setRejectedErrors([
        // @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'never'.
        { uploadError: "incompatible_json_format_error", file },
      ]);
    }
  };

  const loadConfigFromFile = (accepted: $TSFixMe) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({ data: reader.result, file: accepted });
      };
      reader.onerror = reject;
      reader.readAsText(accepted);
    });

  const onDrop = (
    accepted: $TSFixMe,
    /* istanbul ignore next */
    rejected = [],
  ) => {
    if (accepted.length > 1) {
      accepted.forEach((f: $TSFixMe) => {
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ uploadError: string; file: any; }' is not assignable to parameter of type 'never'.
        rejected.push({
          uploadError: "dropzone.mutiple_drops_forbidden",
          file: f,
        });
      });
    }

    const validFile = accepted.pop();
    if (
      !whiteList.find((suffix) => validFile.name.toLowerCase().endsWith(suffix))
    ) {
      // @ts-expect-error TS(2345) FIXME: Argument of type '{ uploadError: string; file: any; }' is not assignable to parameter of type 'never'.
      rejected.push({
        uploadError: "incompatible_type_error",
        file: validFile,
      });
    }

    if (maxFileSize && validFile.size > Number(maxFileSize)) {
      // @ts-expect-error TS(2322) FIXME: Type 'string' is not assignable to type 'never'.
      rejected.push({ uploadError: "size_limit_exceeded", file: validFile });
    }

    setRejectedErrors(rejected);

    if (validFile && rejected.length === 0) {
      if (
        values.modbusConfig?.length !== 1 ||
        values.genericModbusConfig?.length !== 0
      ) {
        setAcceptedFile(validFile);
        setShowImportModal(true);
      } else {
        setAcceptedFile(undefined);
        loadConfigFromFile(validFile).then((value) =>
          updateUIWithLoadedData(value),
        );
      }
    }
  };
  const onConfirmImport = () => {
    setShowImportModal(false);
    loadConfigFromFile(acceptedFile).then((value) =>
      updateUIWithLoadedData(value),
    );
  };

  const onCloseImport = () => {
    setShowImportModal(false);
  };

  const rejectedFilesRendered = rejectedErrors.map((error) => {
    const whiteListReadable = `( ${whiteList.join(", ")} )`;

    const mapErrorToMessage = (err: $TSFixMe) => {
      switch (err) {
        case "size_limit_exceeded":
          return intl.formatMessage(
            { id: "modbus_import_dropzone.size_error" },
            { limit: filesize(maxFileSize) },
          );
        case "incompatible_json_format_error":
          // @ts-expect-error TS(2349) FIXME: This expression is not callable.
          return t`modbus_import_dropzone.modbus_incompatible_json_format`;
        case "incompatible_config_format_error":
          // @ts-expect-error TS(2349) FIXME: This expression is not callable.
          return t`modbus_import_dropzone.modbus_incompatible_config_format`;
        default:
          return intl.formatMessage(
            { id: "modbus_import_dropzone.incompatible_type_error" },
            { whitelist: whiteListReadable },
          );
      }
    };

    return (
      <li id="rejected-file" key={uuid()}>
        <strong>{(error as $TSFixMe).file.name}</strong>:{" "}
        {mapErrorToMessage((error as $TSFixMe).uploadError)}
      </li>
    );
  });

  let rejectionError = null;
  if (rejectedErrors?.length > 0) {
    rejectionError = (
      <div id="rejection-input-alert" className="files-rejection-full-width">
        <ul>
          {rejectedErrors.length > 1
            ? // @ts-expect-error TS(2349) FIXME: This expression is not callable.
              t`dropzone.mutiple_drops_forbidden`
            : rejectedFilesRendered}
        </ul>
      </div>
    );
  }

  return (
    <div id="section_cm44">
      <label
        htmlFor="Modbus Configuration Import"
        data-testid="modbus-config-import"
      >
        {/* @ts-expect-error TS(2349) FIXME: This expression is not callable. */}
        {t`field_gateway.modbus.config.import`}
      </label>
      <FilesUpload
        {...props}
        name="configUpload"
        multiple
        onDrop={onDrop}
        data-testid="fileUploader"
      />
      {rejectionError}
      <ConfirmationModal
        id="import-modal"
        show={showImportModal}
        // @ts-expect-error TS(2349) FIXME: This expression is not callable.
        titleText={t`field_gateway.modbus.config.import.modal.title`}
        // @ts-expect-error TS(2349) FIXME: This expression is not callable.
        messageText={t`field_gateway.modbus.config.import.modal.text`}
        onConfirm={() => onConfirmImport()}
        onClose={onCloseImport}
      />
    </div>
  );
}

export default withIntl(ModbusImport);
